import moment from "moment";
import { defaults } from "lodash";
import set from "@rill/set";
import { AnnualPlanner } from "./AnnualPlanner";
import {
  AddPhaseTemplateModal,
  PhaseTemplatesModal,
  AddEventModal,
} from "../../../../parts/modals/annual-planner";
import { PhaseDescriptionModal } from "../../../../parts/modals/annual-planner/phase-description-modal";
import { RemovePhaseDescriptionModal } from "../../../../parts/modals/annual-planner/remove-phase-description-modal";
import { AddIndividualPhaseModal } from "../../../../parts/modals/annual-planner/add-individial-phase-modal";
import { EditIndividualPhaseModal } from "../../../../parts/modals/annual-planner/edit-individual-phase-modal";
import { EditTemplateModal } from "../../../../parts/modals/annual-planner/edit-template-modal";
import { RemoveDefaultTemplateModal } from "../../../../parts/modals/annual-planner/remove-default-template-modal";
import { IndividualEventModal } from "../../../../parts/modals/annual-planner/individual-event-modal";
import {
  getStartingCalendarDayAdjustment,
  getWeekColumnData,
} from "../../../../utils/annual-planner-helpers";
import { RemoveTemplateModal } from "../../../../parts/modals/annual-planner/remove-template-modal";
import { whoIsAthleteCoach } from "../../../../utils/user-helpers";
import ActivityDrawer from "../../../../parts/activity-drawer/ActivityDrawer";

const AnnualPlannerController = (app) => {
  app.get(
    "/annual-planner",
    set("heading", "Annual Planner"),
    async ({ req, res, locals, load }) => {
      const { query } = req;
      const {
        activeUser: { startingCalendarDay },
        organizationRoleName,
      } = locals;

      defaults(query, {
        from: moment.utc().subtract(3, "months").toDate(),
        to: moment.utc().add(9, "months").toDate(),
        athlete: athleteId,
        coach: locals.activeUser._id,
        statsFrom: getWeekColumnData(
          moment.utc().subtract(1, "years").format("YYYYMMDD"),
          startingCalendarDay,
          0
        ),
      });

      const { athleteSwitch } = query;

      const isAllowToViewAllOrganizationsAthletes =
        organizationRoleName &&
        (organizationRoleName === "Owner" ||
          organizationRoleName === "Admin" ||
          organizationRoleName === "Nutritionist");

      const loadPromises = [];

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        loadPromises.push(load("allAthletesFromOrganization"));
      }

      // This is made to clean the prevFrom value. Thus the data will be loaded til today's date
      // if the condition is true
      // Why? this is made because when the page is reloaded/athlete is switched, the date range
      // in the query params will remain (smthg like: 20210101 - 20220101) and we want to load the data
      // until the current date. Since prevFrom is the limitator here, we clean it
      if (
        (typeof window !== "undefined" &&
          String(window.performance.getEntriesByType("navigation")[0].type) ===
            "reload") ||
        athleteSwitch
      ) {
        query.prevFrom = undefined;
      }

      const {
        phaseDescriptionStatus,
        phaseDescription,
        phase,
        dateAttachment,
        templateId,
        defaultTemplateName,
        defaultTemplateId,
        eventId,
        eventName,
        eventDescription,
        eventFromDate,
        eventToDate,
        eventColor,
        mode = "read",
        eventFrom = "",
        eventTo = "",
        modal = {},
        statsFrom,
        prevFrom,
        templateToRemove,
        organizationCoach = undefined,
        athlete,
      } = query;

      const organizationCoachId = organizationCoach
        ? whoIsAthleteCoach(
            locals.activeUser.athletes,
            athlete,
            locals.activeUser._id,
            organizationCoach
          )
        : undefined;

      const { athlete: athleteId, date } = query;

      loadPromises.push(
        load("phasesTemplates", {
          athleteId,
          coach: locals.activeUser._id,
        }),
        load("athleteVolumes", {
          athleteId,
          monthFrom: moment(statsFrom, "YYYYMMDD").format("YYYYMMDD"),
          monthTo: prevFrom
            ? moment(prevFrom, "YYYYMMDD").format("YYYYMMDD")
            : moment.utc().format("YYYYMMDD"),
        }),
        load("dailyStats", {
          proxyAthlete: athleteId,
          start: moment(statsFrom, "YYYYMMDD").format("YYYYMMDD"),
          end: prevFrom
            ? moment(prevFrom, "YYYYMMDD").format("YYYYMMDD")
            : moment.utc().format("YYYYMMDD"),
        })
      );

      await Promise.all(loadPromises);

      locals.modal = {
        "add-event": (
          <AddEventModal
            from={eventFrom}
            to={eventTo}
            athleteId={athleteId}
            date={date}
            query={query}
            locals={locals}
            organizationCoachId={organizationCoachId}
          />
        ),
        "individual-event": (
          <IndividualEventModal
            eventId={eventId}
            eventName={eventName}
            eventDescription={eventDescription}
            eventFromDate={eventFromDate}
            eventToDate={eventToDate}
            eventColor={eventColor}
            athleteId={athleteId}
            mode={mode}
            organizationCoachId={organizationCoachId}
          />
        ),
        "add-phase-template": (
          <AddPhaseTemplateModal
            from={eventFrom}
            to={eventTo}
            athleteId={athleteId}
            date={date}
            query={query}
            organizationCoachId={organizationCoachId}
          />
        ),
        "phase-templates": (
          <PhaseTemplatesModal
            athleteId={athleteId}
            organizationCoachId={organizationCoachId}
          />
        ),
        "edit-template": (
          <EditTemplateModal
            templateId={templateId}
            athleteId={athleteId}
            organizationCoachId={organizationCoachId}
          />
        ),
        "remove-template": (
          <RemoveTemplateModal
            templateId={templateId}
            athleteId={athleteId}
            templateToRemove={templateToRemove}
            organizationCoachId={organizationCoachId}
          />
        ),
        "add-individual-phase": (
          <AddIndividualPhaseModal
            athleteId={athleteId}
            organizationCoachId={organizationCoachId}
          />
        ),
        "edit-individual-phase": (
          <EditIndividualPhaseModal
            athleteId={athleteId}
            phase={phase}
            organizationCoachId={organizationCoachId}
          />
        ),
        "remove-default-template": (
          <RemoveDefaultTemplateModal
            defaultTemplateName={defaultTemplateName}
            defaultTemplateId={defaultTemplateId}
            organizationCoachId={organizationCoachId}
          />
        ),
        "phase-description": (
          <PhaseDescriptionModal
            athleteId={athleteId}
            dateAttachment={dateAttachment}
            phaseDescriptionStatus={phaseDescriptionStatus}
            phaseDescription={phaseDescription}
            organizationCoachId={organizationCoachId}
          />
        ),
        "remove-phase-description": (
          <RemovePhaseDescriptionModal
            athleteId={athleteId}
            dateAttachment={dateAttachment}
            phaseDescription={phaseDescription}
            organizationCoachId={organizationCoachId}
          />
        ),
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <AnnualPlanner locals={locals} />;
    }
  );
};

export default AnnualPlannerController;
